import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Divider, Stack, Checkbox, FormControlLabel,
    Chip, Autocomplete
} from '@mui/material';
import * as api from './usersApi';
import type {
    User, Role, AddressType, ContactInfoType, Address, ContactInfo,
    Location, Brand
} from './usersApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface UserViewProps {
    userId: number | null;
}

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

export function UserView({ userId }: UserViewProps) {
    const { t } = useTranslation();
    const detail = useDetailView<User>();

    const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
    const [contactInfoTypes, setContactInfoTypes] = useState<ContactInfoType[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);

    const isNew = userId === null;

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            const [addressTypesData, contactInfoTypesData, rolesData, locationsData, brandsData] =
                await Promise.all([
                    api.getAddressTypes(),
                    api.getContactInfoTypes(),
                    api.getValidRoles(),
                    api.getLocations(),
                    api.getBrands(),
                ]);

            setAddressTypes(addressTypesData);
            setContactInfoTypes(contactInfoTypesData);
            setRoles(rolesData);
            setLocations(locationsData);
            setBrands(brandsData);

            if (isNew) {
                const emailType = contactInfoTypesData.find(x => x.systemTypeName === 'Email');
                const newUser: User = {
                    id: -1,
                    firstName: '',
                    lastName: '',
                    username: '',
                    isActive: true,
                    role: null,
                    addresses: [],
                    contactInfos: emailType ? [{ contactInfoType: emailType } as ContactInfo] : [],
                    locations: [],
                    brands: [],
                };
                detail.setItem(newUser);
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const userData = await api.getUser(userId!);
                userData.addresses = userData.addresses ?? [];
                userData.contactInfos = userData.contactInfos ?? [];
                userData.locations = userData.locations ?? [];
                userData.brands = userData.brands ?? [];
                detail.setItem(userData);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [userId, isNew]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const user = detail.item;

    // Validation
    const hasFirstName = (): boolean => !!user?.firstName && user.firstName.length > 0;
    const hasLastName = (): boolean => !!user?.lastName && user.lastName.length > 0;
    const hasUserName = (): boolean => !!user?.username && user.username.length > 0;
    const hasPassword = (): boolean => !detail.isNew || (!!user?.password && user.password.length > 0);
    const hasRole = (): boolean => !!user?.role && user.role.id !== -1;
    const emailInfo = user?.contactInfos.find(c => c.contactInfoType?.systemTypeName === 'Email');
    const hasEmail = (): boolean => !!emailInfo;
    const isEmailValid = (): boolean =>
        !!emailInfo?.value && EMAIL_REGEX.test(emailInfo.value);

    const isAllInformationValid = (): boolean =>
        hasFirstName() && hasLastName() && hasUserName() && hasPassword() &&
        hasEmail() && hasRole() && isEmailValid();

    const availableAddressTypes = addressTypes.filter(at =>
        !(user?.addresses ?? []).some(a => a.addressType?.id === at.id)
    );

    const availableContactInfoTypes = contactInfoTypes.filter(ct =>
        !(user?.contactInfos ?? []).some(c => c.contactInfoType?.id === ct.id)
    );

    // Actions
    const startEdit = () => {
        detail.startEdit();
    };

    const cancelEdit = () => {
        detail.cancelEdit();
    };

    const save = async () => {
        if (!user) return;
        await detail.withSaving(async () => {
            const payload: Partial<User> = {
                ...user,
                brands: user.brands.map(b => ({ id: b.id } as Brand)),
                locations: user.locations.map(l => ({ id: l.id } as Location)),
            };

            if (!payload.startDate) {
                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                payload.startDate = `${yyyy}-${mm}-${dd}`;
            }

            if (detail.isNew) {
                const response = await api.createUser(payload);
                response.addresses = response.addresses ?? [];
                response.contactInfos = response.contactInfos ?? [];
                response.locations = response.locations ?? [];
                response.brands = response.brands ?? [];
                detail.setItem(response);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            } else {
                const response = await api.updateUser(user.id, payload);
                response.addresses = response.addresses ?? [];
                response.contactInfos = response.contactInfos ?? [];
                response.locations = response.locations ?? [];
                response.brands = response.brands ?? [];
                detail.setItem(response);
                detail.setIsEditing(false);
            }
        });
    };

    const addNewAddress = (addressType: AddressType) => {
        if (!user) return;
        detail.update('addresses', [...user.addresses, { addressType } as Address]);
    };

    const removeAddress = (address: Address) => {
        if (!user) return;
        detail.update('addresses', user.addresses.filter(a => a.addressType?.id !== address.addressType?.id));
    };

    const updateAddressField = (address: Address, field: string, value: string) => {
        if (!user) return;
        detail.update('addresses', user.addresses.map(a =>
            a.addressType?.id === address.addressType?.id ? { ...a, [field]: value } : a
        ));
    };

    const addNewContactInfo = (contactInfoType: ContactInfoType) => {
        if (!user) return;
        detail.update('contactInfos', [...user.contactInfos, { contactInfoType } as ContactInfo]);
    };

    const removeContactInfo = (info: ContactInfo) => {
        if (!user) return;
        detail.update('contactInfos', user.contactInfos.filter(c => c.contactInfoType?.id !== info.contactInfoType?.id));
    };

    const updateContactInfoField = (info: ContactInfo, field: string, value: string) => {
        if (!user) return;
        detail.update('contactInfos', user.contactInfos.map(c =>
            c.contactInfoType?.id === info.contactInfoType?.id ? { ...c, [field]: value } : c
        ));
    };

    const displayName = detail.isNew ? t('user.new') : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;

    return (
        <DetailViewShell<User>
            title={displayName}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isAllInformationValid()}
            loading={detail.loading}
            error={detail.error}
            item={user}
            onEdit={startEdit}
            onSave={save}
            onCancel={cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (<>
                {/* First Name */}
                <FormRow label={t('user.firstname')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.firstName}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={item.firstName}
                            onChange={e => detail.update('firstName', e.target.value)}
                            error={!hasFirstName()}
                        />
                    )}
                </FormRow>

                {/* Last Name */}
                <FormRow label={t('user.lastname')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.lastName}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={item.lastName}
                            onChange={e => detail.update('lastName', e.target.value)}
                            error={!hasLastName()}
                        />
                    )}
                </FormRow>

                {/* Username */}
                <FormRow label={t('user.username')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.username}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={item.username}
                            onChange={e => detail.update('username', e.target.value)}
                            error={!hasUserName()}
                        />
                    )}
                </FormRow>

                {/* Password */}
                <FormRow label={t('user.password')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.password}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            type="password"
                            fullWidth
                            autoComplete="new-password"
                            value={item.password ?? ''}
                            onChange={e => detail.update('password', e.target.value)}
                            error={detail.isNew && !hasPassword()}
                        />
                    )}
                </FormRow>

                {/* Username POS */}
                <FormRow label={t('user.usernamepos')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.usernamePos}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={item.usernamePos ?? ''}
                            onChange={e => detail.update('usernamePos', e.target.value)}
                        />
                    )}
                </FormRow>

                {/* Employee Number */}
                <FormRow label={t('user.employeenumber')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.employeeNumber}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={item.employeeNumber ?? ''}
                            onChange={e => detail.update('employeeNumber', e.target.value)}
                        />
                    )}
                </FormRow>

                {/* SSN */}
                <FormRow label={t('user.ssn')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.socialSecurityNumber}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={item.socialSecurityNumber ?? ''}
                            onChange={e => detail.update('socialSecurityNumber', e.target.value)}
                        />
                    )}
                </FormRow>

                {/* Start Date */}
                <FormRow label={t('user.startdate')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{formatDate(item.startDate)}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            type="date"
                            fullWidth
                            value={item.startDate ? item.startDate.substring(0, 10) : ''}
                            onChange={e => detail.update('startDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    )}
                </FormRow>

                {/* Active */}
                <FormRow label={t('user.isactive')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>
                            {item.isActive ? t('common.true') : t('common.false')}
                        </Typography>
                    ) : (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={item.isActive}
                                    onChange={e => detail.update('isActive', e.target.checked)}
                                />
                            }
                            label=""
                        />
                    )}
                </FormRow>

                {/* Common Reports */}
                <FormRow label={t('user.commonreports')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>
                            {item.isAllowedToViewCommonReports ? t('common.true') : t('common.false')}
                        </Typography>
                    ) : (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!item.isAllowedToViewCommonReports}
                                    onChange={e => detail.update('isAllowedToViewCommonReports', e.target.checked)}
                                />
                            }
                            label=""
                        />
                    )}
                </FormRow>

                <Divider />

                {/* Addresses */}
                {item.addresses.map(address => (
                    <FormRow key={address.addressType?.id} label={address.addressType?.typeName ?? ''}>
                        <AddressEditor
                            address={address}
                            isEditing={detail.isEditing}
                            onChange={(field, value) => updateAddressField(address, field, value)}
                            onRemove={() => removeAddress(address)}
                        />
                    </FormRow>
                ))}

                {detail.isEditing && availableAddressTypes.length > 0 && (
                    <FormRow label="">
                        <FormControl size="small" fullWidth>
                            <Select
                                value=""
                                displayEmpty
                                onChange={e => {
                                    const at = addressTypes.find(a => a.id === e.target.value);
                                    if (at) addNewAddress(at);
                                }}
                            >
                                <MenuItem value=""><em>+ {t('user.newaddress')}</em></MenuItem>
                                {availableAddressTypes.map(at => (
                                    <MenuItem key={at.id} value={at.id}>{at.typeName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormRow>
                )}

                {/* Contact Infos */}
                {item.contactInfos.map(info => {
                    const isEmail = info.contactInfoType?.systemTypeName === 'Email';
                    const emailHasError = isEmail && detail.isEditing && (!info.value || !EMAIL_REGEX.test(info.value));
                    return (
                        <FormRow key={info.contactInfoType?.id} label={info.contactInfoType?.typeName ?? ''}>
                            <ContactInfoEditor
                                contactInfo={info}
                                isEditing={detail.isEditing}
                                hasError={emailHasError}
                                errorText={emailHasError ? t('user.entervalidemail') : undefined}
                                onChange={(field, value) => updateContactInfoField(info, field, value)}
                                onRemove={() => removeContactInfo(info)}
                            />
                        </FormRow>
                    );
                })}

                {detail.isEditing && availableContactInfoTypes.length > 0 && (
                    <FormRow label="">
                        <FormControl size="small" fullWidth error={!hasEmail()}>
                            <Select
                                value=""
                                displayEmpty
                                onChange={e => {
                                    const ct = contactInfoTypes.find(c => c.id === e.target.value);
                                    if (ct) addNewContactInfo(ct);
                                }}
                            >
                                <MenuItem value=""><em>+ {t('user.newcontactinfo')}</em></MenuItem>
                                {availableContactInfoTypes.map(ct => (
                                    <MenuItem key={ct.id} value={ct.id}>{ct.typeName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormRow>
                )}

                <Divider />

                {/* Locations */}
                <FormRow label={t('user.locations')}>
                    {!detail.isEditing ? (
                        <Box>
                            {[...item.locations]
                                .sort((a, b) => a.locationName.localeCompare(b.locationName))
                                .map(l => <Typography sx={sx14} key={l.id}>{l.locationName}</Typography>)}
                        </Box>
                    ) : (
                        <Autocomplete
                            multiple
                            size="small"
                            options={locations}
                            getOptionLabel={o => o.locationName}
                            value={item.locations}
                            onChange={(_, value) => detail.update('locations', value)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={params => <TextField {...params} placeholder={t('user.addlocation')} />}
                            renderTags={(value, getTagProps) =>
                                value.map((l, i) => <Chip {...getTagProps({ index: i })} key={l.id} label={l.locationName} size="small" />)
                            }
                        />
                    )}
                </FormRow>

                {/* Brands */}
                <FormRow label={t('user.brands')}>
                    {!detail.isEditing ? (
                        <Box>
                            {[...item.brands]
                                .sort((a, b) => a.brandDescription.localeCompare(b.brandDescription))
                                .map(b => <Typography sx={sx14} key={b.id}>{b.brandDescription}</Typography>)}
                        </Box>
                    ) : (
                        <Autocomplete
                            multiple
                            size="small"
                            options={brands}
                            getOptionLabel={o => `${o.brandCode} ${o.brandDescription}`}
                            value={item.brands}
                            onChange={(_, value) => detail.update('brands', value)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={params => <TextField {...params} placeholder={t('user.addBrand')} />}
                            renderTags={(value, getTagProps) =>
                                value.map((b, i) => (
                                    <Chip {...getTagProps({ index: i })} key={b.id} label={`${b.brandCode} ${b.brandDescription}`} size="small" />
                                ))
                            }
                        />
                    )}
                </FormRow>

                {/* Role */}
                <FormRow label={t('user.roles')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx14}>{item.role?.roleName}</Typography>
                    ) : (
                        <FormControl size="small" fullWidth error={!hasRole()}>
                            <Select
                                value={item.role?.id ?? ''}
                                onChange={e => {
                                    const r = roles.find(x => x.id === e.target.value);
                                    detail.update('role', r ?? null);
                                }}
                            >
                                {[...roles]
                                    .sort((a, b) => a.roleName.localeCompare(b.roleName))
                                    .map(r => (
                                        <MenuItem key={r.id} value={r.id}>{r.roleName}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                </FormRow>
            </>)}
        </DetailViewShell>
    );
}

interface AddressEditorProps {
    address: Address;
    isEditing: boolean;
    onChange: (field: string, value: string) => void;
    onRemove: () => void;
}

function AddressEditor({ address, isEditing, onChange, onRemove }: AddressEditorProps) {
    const { t } = useTranslation();
    const fields: { key: string; label: string }[] = [
        { key: 'addressLine1', label: t('address.addressline1') },
        { key: 'addressLine2', label: t('address.addressline2') },
        { key: 'addressLine3', label: t('address.addressline3') },
        { key: 'postalCode', label: t('address.postalCode') },
        { key: 'city', label: t('address.city') },
        { key: 'country', label: t('address.country') },
    ];

    if (!isEditing) {
        const parts = fields
            .map(f => address[f.key] as string | undefined)
            .filter(Boolean);
        return <Typography sx={sx14}>{parts.join(', ')}</Typography>;
    }

    return (
        <Stack spacing={1}>
            {fields.map(f => (
                <TextField
                    key={f.key}
                    size="small"
                    fullWidth
                    placeholder={f.label}
                    value={(address[f.key] as string | undefined) ?? ''}
                    onChange={e => onChange(f.key, e.target.value)}
                />
            ))}
            <Box>
                <Button size="small" color="error" variant="outlined" onClick={onRemove}>
                    Remove
                </Button>
            </Box>
        </Stack>
    );
}

interface ContactInfoEditorProps {
    contactInfo: ContactInfo;
    isEditing: boolean;
    hasError?: boolean;
    errorText?: string;
    onChange: (field: string, value: string) => void;
    onRemove: () => void;
}

function ContactInfoEditor({ contactInfo, isEditing, hasError, errorText, onChange, onRemove }: ContactInfoEditorProps) {
    if (!isEditing) {
        return <Typography sx={sx14}>{contactInfo.value as string}</Typography>;
    }
    return (
        <Box display="flex" gap={1}>
            <TextField
                size="small"
                fullWidth
                error={hasError}
                helperText={errorText}
                value={(contactInfo.value as string | undefined) ?? ''}
                onChange={e => onChange('value', e.target.value)}
            />
            <Button size="small" color="error" variant="outlined" onClick={onRemove}>
                Remove
            </Button>
        </Box>
    );
}

function formatDate(value: string | undefined): string {
    if (!value) return '';
    return value.substring(0, 10);
}
